// --------------------------------------------------------------
// Created On: 2021-06-01
// Author: Zachary Thomas
//
// Last Modified: 2024-02-01
// Modified By: Zachary Thomas
//
// Copyright 2022 - 2024 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import IconTooltip from "../IconTooltip/IconTooltip";
import PropTypes from "prop-types";
import translateManufacturer from "../../utilities/translateManufacturer";
import styles from "./AssetDetails.module.scss";

// Panel that displays information about the current asset.
export default function AssetDetails(props: Props): Component {
  return (
    <div className="px-3 pt-4 pb-3" data-test="asset-details-container">
      <div className="row justify-content-center">
        <div className="table-responsive col-12 col-xl-8">
          <table className={`${styles.table} table mb-0 pb-0`}>
            <thead className={styles.thead}>
              <tr>
                <th className={styles.th}>Asset Details</th>
                <th className={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Asset Name</td>
                <td>
                  <span className="me-2">{props.name}</span>
                  {props.isRented && (
                    <IconTooltip
                      id="rental"
                      icon="file-text"
                      message="This asset is a rental. Your ability to modify this asset is limited."
                      color="var(--alert-info)"
                    />
                  )}
                </td>
              </tr>

              <tr>
                <td>Asset Nickname</td>
                {(props.nickname !== null && props.nickname !== undefined && props.nickname.length > 0) ||
                props.loading ? (
                  <td>{props.nickname}</td>
                ) : (
                  <td>None</td>
                )}
              </tr>

              {props.productType !== null && props.productType !== undefined && (
                <tr>
                  <td>Product Type</td>
                  <td>{props.productType}</td>
                </tr>
              )}

              <tr>
                <td>Product Manufacturer</td>
                <td>
                  {props.productManufacturer !== null ? translateManufacturer(props.productManufacturer) : "None"}
                </td>
              </tr>

              <tr>
                <td>Product Part Number</td>
                <td>{props.productModel !== null ? props.productModel : "None"}</td>
              </tr>

              <tr>
                <td>Product Serial Number</td>
                <td>{props.productIdentifier !== null ? props.productIdentifier : "None"}</td>
              </tr>

              <tr>
                <td>Controller</td>
                <td>{props.controllerModel !== null ? props.controllerModel : "None"}</td>
              </tr>

              <tr>
                <td>
                  <span className="d-none d-sm-inline">Monitoring Device</span>
                  <span className="d-inline d-sm-none">Device</span>
                </td>
                <td>
                  <span className="me-2">
                    {props.deviceType !== null ? `${props.deviceType} ${props.deviceIdentifier}` : "None"}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

AssetDetails.propTypes = {
  name: PropTypes.string.isRequired,
  nickname: PropTypes.string,
  productType: PropTypes.string,
  productModel: PropTypes.string.isRequired,
  productIdentifier: PropTypes.string.isRequired,
  productManufacturer: PropTypes.string.isRequired,
  deviceId: PropTypes.number,
  deviceType: PropTypes.string,
  deviceIdentifier: PropTypes.string,
  controllerModel: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  isRented: PropTypes.bool.isRequired,
};

interface Props {
  name: string;
  nickname: string | null;
  productType?: string;
  productModel: string;
  productIdentifier: string;
  productManufacturer: string;
  deviceType: string | null;
  deviceId?: number | null;
  deviceIdentifier: string | null;
  controllerModel: string | null;
  loading: boolean;
  isRented: boolean;
}
